import { useEffect, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  LayoutDashboard,
  LogOut,
  Menu,
  Search,
  Users as UsersIcon,
  X,
} from 'lucide-react';
import { Link, useLocation } from '@tanstack/react-router';
import { NotificationsBell } from './NotificationsBell';
import { PostLoginModalBanner } from './PostLoginModalBanner';
import { TopPageBanner } from './TopPageBanner';
import {
  Store,
  Package as PackageIcon,
  Globe,
  Palette,
  Wallet,
  Receipt,
  Settings,
  Ticket,
  HelpCircle,
} from 'lucide-react';
import { Avatar } from '../components/Avatar';
import { ThemeToggle } from './ThemeToggle';
import { cn } from '../lib/cn';
import type { ResellerViewState } from '../mocks/reseller';
import { useApplicantContext, useCurrentReseller } from '../api/reseller';
import { useSession } from '../stores/session';

/**
 * Reseller portal shell. Shows the reseller's brand (display name + accent color),
 * with their own scoped navigation: customers, staff, branding, packages, revenue.
 */
export function ResellerShell({ children }: { children: ReactNode }) {
  const { data: currentApplicantContext } = useApplicantContext();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches) setDrawerOpen(false);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
    return undefined;
  }, [drawerOpen]);

  const view = currentApplicantContext?.view ?? 'active';

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <TopPageBanner />
      <ResellerTopBar onOpenMobileNav={() => setDrawerOpen(true)} />

      {view !== 'active' && <PreActiveBanner view={view} />}

      <div className="flex">
        <aside className="hidden lg:block sticky top-14 self-start h-[calc(100vh-3.5rem)]">
          <ResellerSidebar />
        </aside>

        {drawerOpen && (
          <div className="lg:hidden fixed inset-0 z-50">
            <div
              className="absolute inset-0 bg-zinc-950/40 backdrop-blur-[2px]"
              onClick={() => setDrawerOpen(false)}
              aria-hidden="true"
            />
            <div className="relative w-72 h-full bg-zinc-50 dark:bg-zinc-900 shadow-lg flex">
              <ResellerSidebar className="border-r-0" />
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-md hover:bg-zinc-200 dark:hover:bg-zinc-800 transition"
                aria-label="Close navigation"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <main className="flex-1 min-w-0 pb-20 lg:pb-0">{children}</main>
      </div>

      <ResellerBottomNav />
      <PostLoginModalBanner />
    </div>
  );
}

/* ─────────────────────────────────────────────────── */

/**
 * Pre-active state banner. Shown when the reseller account is not yet `active`
 * (still applying, awaiting decision, or approved-but-not-onboarded). Pages
 * underneath may render incomplete data, so we surface the state everywhere
 * with a single tap-through to the dedicated status page.
 */
function PreActiveBanner({ view }: { view: ResellerViewState }) {
  const isApproved = view === 'pending_setup';
  const tone = isApproved
    ? 'bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-950/40 dark:border-emerald-900 dark:text-emerald-100'
    : 'bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-950/40 dark:border-amber-900 dark:text-amber-100';
  const Icon = isApproved ? CheckCircle2 : AlertTriangle;

  return (
    <div
      role="status"
      className={cn(
        'sticky top-14 z-30 border-b px-4 sm:px-5 py-2.5 text-sm flex items-center gap-3',
        tone,
      )}
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      <span className="flex-1 min-w-0 truncate sm:whitespace-normal">
        Your reseller account is{' '}
        <span className="font-semibold">{humanizeView(view)}</span>.
      </span>
      <a
        href="/reseller/status"
        className="inline-flex items-center gap-1 font-medium hover:underline underline-offset-2 flex-shrink-0"
      >
        View details
        <ArrowRight className="w-3.5 h-3.5" />
      </a>
    </div>
  );
}

function humanizeView(view: ResellerViewState): string {
  switch (view) {
    case 'application_pending':
      return 'awaiting review';
    case 'application_in_review':
      return 'under review';
    case 'application_more_info':
      return 'on hold — more info needed';
    case 'application_rejected':
      return 'not approved';
    case 'application_withdrawn':
      return 'withdrawn';
    case 'pending_setup':
      return 'approved — finish setup';
    case 'active':
      return 'active';
    default:
      return view;
  }
}

/* ─────────────────────────────────────────────────── */

function ResellerTopBar({ onOpenMobileNav }: { onOpenMobileNav?: () => void }) {
  const { data: currentReseller } = useCurrentReseller();
  return (
    <header className="sticky top-0 z-40 h-14 border-b border-zinc-200 bg-white/80 backdrop-blur-md dark:bg-zinc-950/80 dark:border-zinc-700">
      <div className="h-full px-4 sm:px-5 flex items-center gap-3">
        {onOpenMobileNav && (
          <button
            type="button"
            onClick={onOpenMobileNav}
            className="lg:hidden -ml-1 w-9 h-9 grid place-items-center rounded-md text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
            aria-label="Open navigation"
          >
            <Menu className="w-5 h-5" />
          </button>
        )}

        <Link to="/reseller" className="flex items-center gap-2 -ml-1 px-1.5 py-1 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800 transition">
          <span
            className="w-7 h-7 rounded-md grid place-items-center text-white font-semibold text-xs flex-shrink-0"
            style={{ backgroundColor: currentReseller?.primaryColor }}
            aria-hidden="true"
          >
            {currentReseller?.name.charAt(0)}
          </span>
          <span className="font-semibold text-zinc-950 dark:text-zinc-50 tracking-tight">
            {currentReseller?.displayName}
          </span>
        </Link>

        <div className="hidden md:flex flex-1 max-w-md mx-auto">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event('open-command-palette'))}
            aria-label="Open command palette"
            className="w-full inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm text-zinc-500 dark:text-zinc-400 bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-700 transition"
          >
            <Search className="w-4 h-4" />
            <span>Search customers, staff…</span>
            <kbd className="ml-auto px-1.5 py-0.5 rounded text-[10px] font-medium bg-white dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-700">⌘K</kbd>
          </button>
        </div>

        <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
          <ThemeToggle />
          <NotificationsBell />
          <SessionAvatar />
        </div>
      </div>
    </header>
  );
}

/** Signed-in user's avatar + explicit sign-out affordance — never a hardcoded demo name. */
function SessionAvatar() {
  const user = useSession((s) => s.user);
  const display = user?.name?.trim() || user?.email?.split('@')[0] || 'Account';
  return (
    <Link
      to="/logout"
      className="ml-1 inline-flex items-center gap-2 pl-1 pr-1 sm:pr-3 py-1 rounded-full border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
      aria-label={`Sign out ${display}`}
      title={`Sign out (${display})`}
    >
      <Avatar name={display} size="sm" />
      <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-zinc-600 dark:text-zinc-300">
        <LogOut className="w-3.5 h-3.5" />
        Sign out
      </span>
    </Link>
  );
}

function ResellerSidebar({ className }: { className?: string }) {
  const { data: currentReseller } = useCurrentReseller();
  return (
    <nav
      className={cn(
        'w-60 h-full bg-zinc-50 border-r border-zinc-200 dark:bg-zinc-900 dark:border-zinc-700 flex flex-col',
        className,
      )}
      aria-label="Reseller navigation"
    >
      <div className="flex-1 overflow-y-auto nice-scroll px-2.5 py-3 space-y-0.5">
        <Item to="/reseller" icon={<LayoutDashboard className="w-4 h-4" />} label="Overview" exact />
        <Item to="/reseller/customers" icon={<Store className="w-4 h-4" />} label="Customers" />
        <Item to="/reseller/staff" icon={<UsersIcon className="w-4 h-4" />} label="Staff" />

        <Section>Money</Section>
        <Item to="/reseller/revenue" icon={<Wallet className="w-4 h-4" />} label="Revenue" />
        <Item to="/reseller/payments" icon={<Receipt className="w-4 h-4" />} label="Payments" />
        <Item to="/reseller/coupons" icon={<Ticket className="w-4 h-4" />} label="Coupons" />
        <Item to="/reseller/packages" icon={<PackageIcon className="w-4 h-4" />} label="My packages" />

        <Section>Brand</Section>
        <Item to="/reseller/branding" icon={<Palette className="w-4 h-4" />} label="Branding" />
        <Item to="/reseller/domains" icon={<Globe className="w-4 h-4" />} label="Domains" />
      </div>

      <div className="border-t border-zinc-200 dark:border-zinc-700 p-2.5 space-y-0.5">
        <Item to="/reseller/settings" icon={<Settings className="w-4 h-4" />} label="Settings" />
        <a
          href={`mailto:${currentReseller?.supportEmail ?? ''}`}
          className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-sm transition text-zinc-500 dark:text-zinc-400 hover:bg-white dark:hover:bg-zinc-800/70 hover:text-zinc-950 dark:hover:text-zinc-50"
        >
          <span className="text-zinc-500 dark:text-zinc-400 flex-shrink-0">
            <HelpCircle className="w-4 h-4" />
          </span>
          <span className="flex-1 truncate">Help & support</span>
        </a>
        <Item to="/logout" icon={<LogOut className="w-4 h-4" />} label="Sign out" muted />
      </div>
    </nav>
  );
}

function Section({ children }: { children: ReactNode }) {
  return (
    <div className="px-2.5 mt-5 mb-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
      {children}
    </div>
  );
}

function Item({
  to,
  icon,
  label,
  exact = false,
  muted = false,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  exact?: boolean;
  muted?: boolean;
}) {
  const location = useLocation();
  const path = location.pathname;
  const active = exact ? path === to : path === to || path.startsWith(`${to}/`);

  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-sm transition',
        active
          ? 'bg-white dark:bg-zinc-800 text-zinc-950 dark:text-zinc-50 shadow-xs font-medium'
          : muted
          ? 'text-zinc-500 dark:text-zinc-400 hover:bg-white dark:hover:bg-zinc-800/70 hover:text-zinc-950 dark:hover:text-zinc-50'
          : 'text-zinc-700 dark:text-zinc-200 hover:bg-white dark:hover:bg-zinc-800/70 hover:text-zinc-950 dark:hover:text-zinc-50',
      )}
    >
      <span className={cn('flex-shrink-0', active ? 'text-brand dark:text-brand-dm' : 'text-zinc-500 dark:text-zinc-400')}>
        {icon}
      </span>
      <span className="flex-1 min-w-0 truncate">{label}</span>
    </Link>
  );
}

function ResellerBottomNav() {
  const location = useLocation();
  const path = location.pathname;
  const items: Array<{ to: string; icon: ReactNode; label: string; match: (p: string) => boolean }> = [
    { to: '/reseller', icon: <LayoutDashboard className="w-5 h-5" />, label: 'Home', match: (p) => p === '/reseller' },
    { to: '/reseller/customers', icon: <Store className="w-5 h-5" />, label: 'Customers', match: (p) => p.startsWith('/reseller/customers') },
    { to: '/reseller/staff', icon: <UsersIcon className="w-5 h-5" />, label: 'Staff', match: (p) => p.startsWith('/reseller/staff') },
    { to: '/reseller/revenue', icon: <Wallet className="w-5 h-5" />, label: 'Revenue', match: (p) => p.startsWith('/reseller/revenue') },
    { to: '/reseller/branding', icon: <Palette className="w-5 h-5" />, label: 'Brand', match: (p) => p.startsWith('/reseller/branding') || p.startsWith('/reseller/domains') },
  ];

  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white dark:bg-zinc-950 border-t border-zinc-200 dark:border-zinc-700 pb-safe"
      aria-label="Bottom navigation"
    >
      <div className="grid grid-cols-5 h-16">
        {items.map((it) => {
          const active = it.match(path);
          return (
            <Link
              key={it.to}
              to={it.to}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'flex flex-col items-center justify-center gap-0.5 transition',
                active
                  ? 'text-brand dark:text-brand-dm'
                  : 'text-zinc-500 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-zinc-50',
              )}
            >
              {it.icon}
              <span className="text-[11px] font-medium">{it.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
